import { BillingServiceTag, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { joinTokenString } from "@/utils/medplum";

export interface ServiceTableColumn {
  label: string;
  align?: "left" | "center" | "right";
}

export const SERVICE_TABLE_COLUMNS: ServiceTableColumn[] = [
  { label: "Name" },
  { label: "Date" },
  { label: "Status" },
  { label: "Provider" },
  { label: "Procedure, Service, or Supply" },
  { label: "Fees", align: "right" },
  { label: "Actions", align: "right" },
];

/** Page size for the client Services (ChargeItem) table. */
export const SERVICES_PAGE_SIZE = 20;

/** FHIR ChargeItem.status values mapped by the Services table. */
export const ChargeItemResourceStatus = {
  Billable: "billable",
  Billed: "billed",
} as const;

/** Display labels for ChargeItem.status in the Services table. */
export const ChargeItemStatusDisplay = {
  Unbilled: "Unbilled",
  Billed: "Billed",
  Unknown: "--",
} as const;

/** CodeSystem for ChargeItem service tags on meta.tag. Searchable via `_tag`. */
export const CHARGE_ITEM_ARCHIVED_TAG_SYSTEM = QuiltedHealthCodeSystems.BillingServiceTags;

export const CHARGE_ITEM_ARCHIVED_TAG_CODE = BillingServiceTag.Archived;

/**
 * The archive tag as a `system|code` token. Used with `_tag:not` to exclude archived ChargeItems
 * and with `_tag` to select only them. Both halves are non-empty enum members, so `joinTokenString`
 * always returns a string here.
 */
export const CHARGE_ITEM_ARCHIVED_TAG = joinTokenString(
  CHARGE_ITEM_ARCHIVED_TAG_SYSTEM,
  CHARGE_ITEM_ARCHIVED_TAG_CODE,
) as string;

/** Services tab filter values persisted in the URL `?filter=` param. */
export enum ServiceFilter {
  All = "all",
  Unbilled = "unbilled",
  Billed = "billed",
  Archived = "archived",
}

/** Default Services list view when `?filter=` is absent. */
export const DEFAULT_SERVICE_FILTER = ServiceFilter.Unbilled;

export const SERVICE_FILTER_OPTIONS: { value: ServiceFilter; label: string }[] = [
  { value: ServiceFilter.All, label: "All" },
  { value: ServiceFilter.Unbilled, label: ChargeItemStatusDisplay.Unbilled },
  { value: ServiceFilter.Billed, label: ChargeItemStatusDisplay.Billed },
  { value: ServiceFilter.Archived, label: "Archived" },
];

/** Values of `ServiceFeeMissingReason`. */
export const SERVICE_FEE_MISSING_REASON = {
  /** The service's code has no ChargeItemDefinition in the fee schedule. */
  NotInFeeSchedule: "not-in-fee-schedule",
  /** The code is in the fee schedule but has no price for the service's payer type. */
  NoFeeForPayer: "no-fee-for-payer",
} as const;

/** The message shown under the code for each missing-fee reason (brief §2.2). */
export const SERVICE_FEE_MISSING_MESSAGE = {
  [SERVICE_FEE_MISSING_REASON.NotInFeeSchedule]: "CPT code not found in fee schedule.",
  [SERVICE_FEE_MISSING_REASON.NoFeeForPayer]: "Fee amount missing for this code.",
} as const;
